import { Box, Button, Checkbox, Chip, FormControlLabel, Paper, Stack, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import type { BookingDraft, LaneOption } from "../../pages/admin/bookingDraftUtils";
import {
  calculateDraftSubtotal,
  formatCurrency,
  getDraftCardDateLabel,
  getDraftCardTimeLabel,
  getSameDayTimeChip,
} from "../../pages/admin/bookingDraftUtils";

interface AdminBookingCardProps {
  draft: BookingDraft;
  index: number;
  isFirst: boolean;
  lanes: LaneOption[];
  discountPercent: number;
  onEdit: () => void;
  onRemove: () => void;
  onToggleSameDay?: (checked: boolean) => void;
  onToggleSameTime?: (checked: boolean) => void;
}

const barColor = (type?: string) => {
  const normalized = (type || "").toLowerCase();
  if (normalized === "lane") return "primary.main";
  if (normalized === "full_facility") return "secondary.dark";
  return "secondary.main";
};

const AdminBookingCard = ({
  draft,
  index,
  isFirst,
  lanes,
  discountPercent,
  onEdit,
  onRemove,
  onToggleSameDay,
  onToggleSameTime,
}: AdminBookingCardProps) => {
  const lane = lanes.find((item) => item.id === draft.selectedLaneId);
  const chip = getSameDayTimeChip(draft, isFirst);
  const price = calculateDraftSubtotal(draft, lanes, discountPercent);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          bgcolor: barColor(lane?.type),
        }}
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
        sx={{ pl: 1.5 }}
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
            {lane?.name || `Booking ${index + 1}`}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center" }}>
              <EventIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {getDraftCardDateLabel(draft)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center" }}>
              <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {getDraftCardTimeLabel(draft)}
            </Typography>
          </Stack>
          {chip && (
            <Chip
              size="small"
              label={chip}
              color={chip === "Different day" ? "default" : "secondary"}
              sx={{ mt: 1 }}
            />
          )}
          {!isFirst && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={draft.sameDay}
                    onChange={(event) => onToggleSameDay?.(event.target.checked)}
                  />
                }
                label="Same day"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={draft.sameTime}
                    onChange={(event) => onToggleSameTime?.(event.target.checked)}
                  />
                }
                label="Same time"
              />
            </Stack>
          )}
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mr: { xs: "auto", sm: 0 } }}>
            {draft.startTime && draft.endTime ? formatCurrency(price) : "—"}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={onEdit}
            sx={{ flex: { xs: 1, sm: "none" } }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<DeleteOutlineIcon />}
            onClick={onRemove}
            sx={{ flex: { xs: 1, sm: "none" } }}
          >
            Remove
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default AdminBookingCard;
